import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  Box,
  IconButton,
  Tooltip,
  TextField,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  FormControl,
  Select,
  TableSortLabel,
} from '@mui/material';
import { MoreVert, Undo, Redo } from '@mui/icons-material';
import { Estimate } from '../../pages/EstimatesPage';
import { updateRms, deleteRms, getEstimate } from '../../services/estimateService';
import { useEstimateData } from '../../contexts/EstimateDataContext';
import { useUndoRedo } from '../../hooks/useUndoRedo';

interface RMSTabProps { estimate: Estimate; positions: any[]; }

type RmsType = 'R' | 'M' | 'S';

interface RmsRow {
  id: number;
  type: RmsType;
  code: string; // ID z bazy
  name: string;
  unit: string;
  price: number;
  quantity: number;
  value: number;
  positions: string[]; // lista nr pozycji gdzie występuje
}

// Placeholder generator – tworzy duplikaty i potem je zagreguje
const generateSampleRms = (): RmsRow[] => {
  const names = [
    // Robocizna (R)
    { type: 'R', name: 'Robocizna', unit: 'r-g' },
    { type: 'R', name: 'Cieśla', unit: 'r-g' },
    { type: 'R', name: 'Zbrojarz', unit: 'r-g' },
    { type: 'R', name: 'Murarz', unit: 'r-g' },
    { type: 'R', name: 'Tynkarz', unit: 'r-g' },
    { type: 'R', name: 'Hydraulik', unit: 'r-g' },
    { type: 'R', name: 'Elektryk', unit: 'r-g' },
    { type: 'R', name: 'Operator koparki', unit: 'r-g' },
    { type: 'R', name: 'Zacieracz podłóg', unit: 'r-g' },
    { type: 'R', name: 'Malowanie', unit: 'r-g' },
    // Materiały (M)
    { type: 'M', name: 'Beton B20', unit: 'm3' },
    { type: 'M', name: 'Beton B30', unit: 'm3' },
    { type: 'M', name: 'Stal zbrojeniowa Ø12', unit: 'kg' },
    { type: 'M', name: 'Stal zbrojeniowa Ø16', unit: 'kg' },
    { type: 'M', name: 'Bloczek silikatowy 24 cm', unit: 'szt' },
    { type: 'M', name: 'Wełna mineralna 15 cm', unit: 'm2' },
    { type: 'M', name: 'Folia paroizolacyjna', unit: 'm2' },
    { type: 'M', name: 'Gładź gipsowa', unit: 'kg' },
    { type: 'M', name: 'Farba akrylowa', unit: 'l' },
    { type: 'M', name: 'Płyta GK 12,5 mm', unit: 'm2' },
    // Sprzęt (S)
    { type: 'S', name: 'Koparka 0,4 m3', unit: 'm-g' },
    { type: 'S', name: 'Koparko-ładowarka', unit: 'm-g' },
    { type: 'S', name: 'Dźwig 20 t', unit: 'm-g' },
    { type: 'S', name: 'Walec 3 t', unit: 'm-g' },
    { type: 'S', name: 'Betoniarka 250l', unit: 'm-g' },
    { type: 'S', name: 'Pompa do betonu', unit: 'm-g' },
    { type: 'S', name: 'Żuraw wieżowy', unit: 'm-g' },
    { type: 'S', name: 'Rusztowanie modułowe', unit: 'm2-g' },
    { type: 'S', name: 'Agregat tynkarski', unit: 'm-g' },
    { type: 'S', name: 'Agregat malarski', unit: 'm-g' },
  ];
  const arr: RmsRow[] = [];
  for (let i = 0; i < 300; i++) {
    const item = names[i % names.length];
    const qty = parseFloat((Math.random() * 500 + 10).toFixed(3));
    const price = parseFloat((Math.random() * 300 + 20).toFixed(2));
    arr.push({
      id: i + 1,
      type: item.type as RmsType,
      code: `ID${1000 + i}`,
      name: item.name,
      unit: item.unit,
      price,
      quantity: qty,
      value: parseFloat((qty * price).toFixed(2)),
      positions: [`${(i % 40) + 1}`, `${((i + 10) % 40) + 1}`],
    });
  }
  // agregacja po name & type
  const map = new Map<string, RmsRow>();
  arr.forEach((r: RmsRow) => {
    const key = r.type + '|' + r.name;
    if (!map.has(key)) {
      map.set(key, { ...r });
    } else {
      const existing = map.get(key)!;
      existing.quantity += r.quantity;
      existing.value += r.value;
      existing.positions = Array.from(new Set([...existing.positions, ...r.positions]));
    }
  });
  // sort by type R, M, S then alphabetical name
  const order: RmsType[] = ['R', 'M', 'S'];
  return Array.from(map.values()).sort((a, b) => {
    if (a.type !== b.type) return order.indexOf(a.type) - order.indexOf(b.type);
    return a.name.localeCompare(b.name);
  });
};

// Helper – aggregate RMS rows from estimate data
const buildRowsFromEstimate = (estimate: Estimate): RmsRow[] => {
  // brak pozycji – zwróć pustą tablicę
  if(!estimate || !(estimate as any).positions) return [];

  type RmsApi = {
    id: number;
    rms_type: 'R'|'M'|'S'|'Kp'|'Kz'|'Z';
    description: string;
    unit: string;
    quantity: number;
    rms_price: number;
  };

  const rowsMap = new Map<string, RmsRow>();
  let idx = 1;

  const positions: any[] = (estimate as any).positions;
  positions.forEach((pos: any) => {
    const posLabel = pos.seq_no !== undefined ? pos.seq_no.toString() : (pos.nr ? pos.nr.toString() : pos.id.toString());
    (pos.rms||[] as RmsApi[]).forEach((r: RmsApi) => {
      if(!['R','M','S'].includes(r.rms_type)) return; // pomiń Kp/Kz/Z
      const key = `${r.rms_type}|${r.description}|${r.unit}|${parseFloat(r.rms_price as any)}`;
      if(!rowsMap.has(key)){
        rowsMap.set(key, {
          id: idx++,
          type: r.rms_type as RmsType,
          code: r.id.toString(),
          name: r.description,
          unit: r.unit,
          price: parseFloat((r.rms_price as unknown as string)) || 0,
          quantity: 0,
          value: 0,
          positions: []
        });
      }
      const row = rowsMap.get(key)!;
      const qty = parseFloat((r.quantity as unknown as string)) || 0;
      row.quantity += qty;
      if(!row.positions.includes(posLabel)) row.positions.push(posLabel);
    });
  });

  const order: RmsType[] = ['R','M','S'];
  const list = Array.from(rowsMap.values());
  list.forEach(r => { r.value = parseFloat((r.price * r.quantity).toFixed(2)); });
  list.sort((a,b)=>{
    if(a.type!==b.type) return order.indexOf(a.type) - order.indexOf(b.type);
    return a.name.localeCompare(b.name);
  });
  // zresetuj id po sortowaniu
  list.forEach((r,i)=>{ r.id = i+1; });
  return list;
};

const buildRowsFromPositions = (positions:any[]): RmsRow[] => {
  const rowsMap = new Map<string, RmsRow>();
  let idx=1;
  positions.forEach(pos=>{
    (pos.rms||[]).forEach((r:any)=>{
      if(!['R','M','S'].includes(r.rms_type)) return;
      const key = `${r.rms_type}|${r.description}|${r.unit}|${parseFloat(r.rms_price)}`;
      if(!rowsMap.has(key)){
        rowsMap.set(key, {
          id: idx++,
          type: r.rms_type,
          code: r.id?.toString()||idx.toString(),
          name: r.description,
          unit: r.unit,
          price: parseFloat(r.rms_price),
          quantity: 0,
          value:0,
          positions:[]
        });
      }
      const row = rowsMap.get(key)!;
      row.quantity += parseFloat(r.quantity);
      row.value = parseFloat((row.price*row.quantity).toFixed(2));
      if(!row.positions.includes(pos.nr)) row.positions.push(pos.nr);
    });
  });
  const order:['R','M','S']=['R','M','S'];
  const list=Array.from(rowsMap.values()).sort((a,b)=>{
    if(a.type!==b.type) return order.indexOf(a.type)-order.indexOf(b.type);
    return a.name.localeCompare(b.name);
  });
  list.forEach((r,i)=>r.id=i+1);
  return list;
};

const RMSTab: React.FC<RMSTabProps> = ({ estimate, positions }) => {
  // Jeśli pozycje mają children zamiast rms, zmapuj:
  const localPositions = positions.map(pos => ({
    ...pos,
    rms: pos.children || pos.rms || []
  }));
  // Używaj localPositions do buildRowsFromPositions:
  const initialRows = useMemo(()=>{
    const result = buildRowsFromPositions(localPositions);
    return result;
  },[localPositions]);

  const { state: rows, setState: setRows, undo, redo, canUndo, canRedo } = useUndoRedo<RmsRow[]>(initialRows);

  // Ref do kontenera tabeli – potrzebny do przewijania
  const tableContainerRef = useRef<HTMLDivElement>(null);

  // Uaktualnij, gdy estimate się zmieni (np. po odświeżeniu)
  useEffect(()=>{ setRows(()=>buildRowsFromPositions(localPositions)); }, [localPositions, setRows]);

  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<RmsType | 'ALL'>('ALL');

  // refs to search & filter controls
  const searchInputRef = useRef<HTMLInputElement>(null);
  const filterSelectRef = useRef<HTMLInputElement>(null);
  const [selectedRowId, setSelectedRowId] = useState<number | null>(null);
  const [selectedCell, setSelectedCell] = useState<{id:number; field:'name'|'unit'|'price'} | null>(null);
  const [editingCell, setEditingCell] = useState<{id:number; field:'name'|'unit'|'price'; value:string} | null>(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Pomocnicza funkcja przewijania aktywnego wiersza w zasięg widoku
  const scrollRowIntoView = (id:number)=>{
    const rowEl = document.querySelector(`[data-rowid="row-${id}"]`) as HTMLElement | null;
    const container = tableContainerRef.current;
    if(rowEl && container){
      const bottom = rowEl.offsetTop + rowEl.offsetHeight;
      if(bottom > container.scrollTop + container.clientHeight){
        container.scrollTop = bottom - container.clientHeight;
      }
      if(rowEl.offsetTop < container.scrollTop){
        const headerH = (container.querySelector('thead') as HTMLElement)?.offsetHeight || 0;
        container.scrollTop = rowEl.offsetTop - headerH;
      }
    }
  };

  // Sortowanie
  const [sortField, setSortField] = useState<'nr'|'name'|'quantity'|'value'>('nr');
  const [sortDir, setSortDir] = useState<'asc'|'desc'>('asc');

  const handleSort = (field:'nr'|'name'|'quantity'|'value')=>{
    if(sortField===field){ setSortDir(prev=> prev==='asc'?'desc':'asc'); }
    else { setSortField(field); setSortDir('asc'); }
  };

  const handleFilterChange = (e: any) => {
    setFilter(e.target.value as any);
  };

  const filtered = rows.filter(r =>
    (filter === 'ALL' || r.type === filter) &&
    (r.name.toLowerCase().includes(search.toLowerCase()) || r.code.toLowerCase().includes(search.toLowerCase()))
  );

  const sortedRows = React.useMemo(()=>{
    const arr=[...filtered];
    arr.sort((a,b)=>{
      let av:any,bv:any;
      switch(sortField){
        case 'nr': av=a.id; bv=b.id; break;
        case 'name': av=a.name.toLowerCase(); bv=b.name.toLowerCase(); break;
        case 'quantity': av=a.quantity; bv=b.quantity; break;
        case 'value': av=a.value; bv=b.value; break;
      }
      if(av<bv) return sortDir==='asc'?-1:1;
      if(av>bv) return sortDir==='asc'?1:-1;
      return 0;
    });
    return arr;
  }, [filtered, sortField, sortDir]);

  const totalValue = sortedRows.reduce((sum, r) => sum + r.value, 0);

  const applyLocalUpdate = (id:number, changes:Partial<RmsRow>)=>{
    setRows(prev=> prev.map(r=> r.id===id ? {...r, ...changes} : r));
  };

  const getDbId = (rowId:number)=>{
    const row = rows.find(r=>r.id===rowId);
    return row ? parseInt(row.code) : rowId;
  };

  const handlePersist = async (id:number, payload:Partial<{description:string; unit:string; rms_price:number}>)=>{
    try{
      await updateRms(estimate.id, getDbId(id), payload);
    }catch(err){ console.error('[RMSTab] update error', err); }
  };

  const updateDetailedRowsContext = (predicate:(exp:any)=>boolean, updater:(exp:any)=>any)=>{
    const prev = (positions||[]) as any[];
    const newRows = prev.map(pos=>({
      ...pos,
      children: (pos.children||[]).map((exp:any)=> predicate(exp)? updater(exp):exp)
    }));
    // setSectionData('detailedRows', newRows); // This line was removed as per the new_code
  };

  const updateRowField = async (id:number, field:'name'|'unit'|'price', value:string|number)=>{
    if(field==='price'){
      const num = typeof value==='number' ? value : parseFloat((value as string).replace(',','.')) || 0;
      const rowPrev = rows.find(r=>r.id===id);
      if(!rowPrev) return;
      const newValue = field==='price' ? parseFloat((num * rowPrev.quantity).toFixed(2)) : parseFloat((num * rowPrev.price).toFixed(2));
      applyLocalUpdate(id, {
        [field]: num,
        value: newValue
      } as any);
      if(field==='price') {
        // MASOWA AKTUALIZACJA: zmień cenę we wszystkich RMS o tej samej nazwie, jednostce i starej cenie
        await updateRms(
          estimate.id,
          parseInt(rowPrev.code),
          {
            rms_price: num,
            massUpdate: true,
            description: rowPrev.name,
            unit: rowPrev.unit,
            old_price: rowPrev.price
          }
        );
      }
      // Po edycji ceny – refetch kosztorysu i aktualizacja detailedRows
      if (typeof getEstimate === 'function') {
        const fresh = await getEstimate(estimate.id);
        // setSectionData('detailedRows', fresh.positions.map((p:any)=>({
        //   ...p,
        //   children: p.rms
        // }))); // This line was removed as per the new_code
      }
    } else if(field==='name'){
      applyLocalUpdate(id,{ name:value as string });
      handlePersist(id,{ description:value as string });

      updateDetailedRowsContext(
        (exp)=> exp.id === getDbId(id),
        (exp)=>({ ...exp, description: value })
      );
    } else if(field==='unit'){
      applyLocalUpdate(id,{ unit:value as string });
      handlePersist(id,{ unit:value as string });

      updateDetailedRowsContext(
        (exp)=> exp.id === getDbId(id),
        (exp)=>({ ...exp, unit: value })
      );
    }
  };

  // Menu
  const handleMenuOpen = (e: React.MouseEvent<HTMLButtonElement>, id: number) => {
    setSelectedRowId(id);
    setMenuAnchorEl(e.currentTarget);
  };
  const handleMenuClose = () => setMenuAnchorEl(null);

  const handleShowPositions = () => {
    setDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteRow = async () => {
    if(selectedRowId===null) return;
    try{
      await deleteRms(estimate.id, getDbId(selectedRowId));
      setRows(prev=> prev.filter(r=> r.id!==selectedRowId));
    }catch(err){ console.error('[RMSTab] delete error', err); }
    handleMenuClose();
  };

  const selectedObj = rows.find(r => r.id === selectedRowId);

  // klawiatura
  const handleKeyDown = React.useCallback((e: React.KeyboardEvent)=>{
    if(searchInputRef.current && searchInputRef.current.contains(e.target as Node)) return;
    if(filterSelectRef.current && (filterSelectRef.current as HTMLElement).contains && (filterSelectRef.current as HTMLElement).contains(e.target as Node)) return;
    if(editingCell){
      if(e.key==='Enter'){
        if(editingCell.field==='price'){
          const numeric = parseFloat(editingCell.value.replace(',','.'))||0;
          updateRowField(editingCell.id, editingCell.field, numeric);
        } else {
          updateRowField(editingCell.id, editingCell.field, editingCell.value);
        }
        setEditingCell(null);
      }
      if(e.key==='Escape') setEditingCell(null);
      return;
    }
    if(selectedCell){
      const idx = filtered.findIndex(r=> r.id===selectedCell.id);
      if(e.key==='ArrowDown' && idx<filtered.length-1){ e.preventDefault(); setSelectedCell({id:filtered[idx+1].id, field:selectedCell.field}); setSelectedRowId(filtered[idx+1].id); scrollRowIntoView(filtered[idx+1].id);} 
      if(e.key==='ArrowUp' && idx>0){ e.preventDefault(); setSelectedCell({id:filtered[idx-1].id, field:selectedCell.field}); setSelectedRowId(filtered[idx-1].id); scrollRowIntoView(filtered[idx-1].id);} 
      const fields: ('name'|'unit'|'price')[] = ['name','unit','price'];
      const colIdx = fields.indexOf(selectedCell.field);
      if((e.key==='ArrowRight' || (e.key==='Tab' && !e.shiftKey)) && colIdx < fields.length-1){
        e.preventDefault();
        setSelectedCell({id:selectedCell.id, field:fields[colIdx+1]});
      }
      if((e.key==='ArrowLeft' || (e.key==='Tab' && e.shiftKey)) && colIdx > 0){
        e.preventDefault();
        setSelectedCell({id:selectedCell.id, field:fields[colIdx-1]});
      }
      if(e.key==='Enter' || e.key==='F2'){
        const row=rows.find(r=>r.id===selectedCell.id);
        if(row) setEditingCell({id:row.id, field:selectedCell.field, value: (row as any)[selectedCell.field].toString()});
      }
      // global shortcuts inside table when no editingCell/selectedCell
      if(e.key==='Tab' && editingCell){ /* handled elsewhere */ }
      if(e.key==='Escape'){
        e.preventDefault();
        setSelectedCell(null); // wróć do zaznaczonego wiersza
      }
      if(e.key.length===1){
        e.preventDefault();
        const row=rows.find(r=>r.id===selectedCell.id); if(row) setEditingCell({id:row.id, field:selectedCell.field, value:e.key});
      }
      return;
    }
    if(!editingCell && !selectedCell){
      if(e.key==='s' || e.key==='S'){ e.preventDefault(); searchInputRef.current?.focus(); return; }
      if(e.key==='f' || e.key==='F'){ e.preventDefault(); filterSelectRef.current?.focus(); return; }
    }

    if(!selectedRowId && (e.key==='ArrowDown' || e.key==='ArrowUp')){
      if(filtered.length>0){
        setSelectedRowId(filtered[0].id);
        scrollRowIntoView(filtered[0].id);
      }
      return;
    }

    // wiersze
    const idx = filtered.findIndex(r=> r.id===selectedRowId);
    if(e.key==='ArrowDown' && idx<filtered.length-1){ e.preventDefault(); setSelectedRowId(filtered[idx+1].id); scrollRowIntoView(filtered[idx+1].id);} 
    if(e.key==='ArrowUp' && idx>0){ e.preventDefault(); setSelectedRowId(filtered[idx-1].id); scrollRowIntoView(filtered[idx-1].id);} 
    if((e.key==='Enter'||e.key==='F2') && selectedRowId){ setSelectedCell({id:selectedRowId, field:'name'});} 
    if(e.key.length===1 && selectedRowId){
      e.preventDefault();
      setSelectedCell({id:selectedRowId, field:'price'}); setEditingCell({id:selectedRowId, field:'price', value:e.key});
    }
  }, [editingCell, selectedCell, selectedRowId, filtered, rows]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && !e.shiftKey && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        if (canUndo) undo();
      }
      if (((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'y') || ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === 'z')) {
        e.preventDefault();
        if (canRedo) redo();
      }
      // Deleguj nawigację jeśli fokus jest w tabeli (lub gdziekolwiek) i nie edytujemy inputa
      if(!(e.target as HTMLElement).closest('input') && !(e.target as HTMLElement).isContentEditable){
        handleKeyDown(e as unknown as React.KeyboardEvent);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [canUndo, canRedo, undo, redo, handleKeyDown]);

  return (
    <Box sx={{ height: '100%' }} tabIndex={0} onKeyDown={handleKeyDown}>
      {/* Toolbar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Tooltip title="Cofnij (⌘Z)"><span><IconButton size="small" onClick={undo} disabled={!canUndo}><Undo fontSize="small" /></IconButton></span></Tooltip>
        <Tooltip title="Ponów (⌘Y)"><span><IconButton size="small" onClick={redo} disabled={!canRedo}><Redo fontSize="small" /></IconButton></span></Tooltip>
        <Box sx={{ display:'flex', alignItems:'center', gap:1, ml:'auto' }}>
          <FormControl size="small" sx={{ minWidth:220 }}>
            <Select
              inputRef={filterSelectRef}
              value={filter}
              onChange={handleFilterChange}
              displayEmpty
              renderValue={(val)=>{
                switch(val){
                  case 'R': return 'Robocizna';
                  case 'M': return 'Materiał';
                  case 'S': return 'Sprzęt';
                  default: return 'Wszystkie';
                }
              }}
              onKeyDown={e => {
                e.stopPropagation();
                if (e.key === 'Escape') {
                  tableContainerRef.current?.focus();
                }
              }}
            >
              <MenuItem value="ALL">Wszystkie</MenuItem>
              <MenuItem value="R">Robocizna</MenuItem>
              <MenuItem value="M">Materiał</MenuItem>
              <MenuItem value="S">Sprzęt</MenuItem>
            </Select>
          </FormControl>
          <TextField
            size="small"
            inputRef={searchInputRef}
            placeholder="Search…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            sx={{ width: 220 }}
            onKeyDown={e => {
              e.stopPropagation();
              if (e.key === 'Escape') {
                tableContainerRef.current?.focus();
              }
            }}
          />
        </Box>
      </Box>

      <TableContainer component={Box} ref={tableContainerRef} sx={{ maxHeight: 'calc(100vh - 260px)', overflowY:'auto', borderRadius:0, pb:10 }}>
        <Table stickyHeader size="small" sx={{
          tableLayout:'auto',
          fontSize:'0.70rem',
          '& .MuiTableCell-root': { padding:'3px 6px' },
          '& thead .MuiTableCell-root': { fontSize:'0.78rem', fontWeight:600 }
        }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width:70,cursor:'pointer' }} onClick={()=>handleSort('nr')}><TableSortLabel active={sortField==='nr'} direction={sortDir}>Nr</TableSortLabel></TableCell>
              <TableCell sx={{ width:30 }}>T</TableCell>
              <TableCell sx={{ width:80 }}>ID</TableCell>
              <TableCell sx={{ cursor:'pointer' }} onClick={()=>handleSort('name')}><TableSortLabel active={sortField==='name'} direction={sortDir}>Nazwa nakładu</TableSortLabel></TableCell>
              <TableCell sx={{ width:70 }}>Jmn</TableCell>
              <TableCell align="right" sx={{ width:110 }}>Cena</TableCell>
              <TableCell align="right" sx={{ width:110,cursor:'pointer' }} onClick={()=>handleSort('quantity')}><TableSortLabel active={sortField==='quantity'} direction={sortDir}>Ilość</TableSortLabel></TableCell>
              <TableCell align="right" sx={{ width:120,cursor:'pointer' }} onClick={()=>handleSort('value')}><TableSortLabel active={sortField==='value'} direction={sortDir}>Wartość</TableSortLabel></TableCell>
              <TableCell align="center" sx={{ width:40 }}>A</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedRows.map((row, idx) => (
              <TableRow
                key={row.id}
                tabIndex={0}
                data-rowid={`row-${row.id}`}
                selected={selectedRowId===row.id && !selectedCell}
                onClick={()=>{setSelectedRowId(row.id); setSelectedCell(null);} }
                sx={ selectedRowId===row.id && !selectedCell ? { outline:'2px solid #1a73e8', outlineOffset:-2 } : {} }
              >
                <TableCell>{idx + 1}</TableCell>
                <TableCell>{row.type}</TableCell>
                <TableCell>{row.code}</TableCell>
                <TableCell
                  sx={selectedCell?.id===row.id&&selectedCell.field==='name'?{outline:'2px solid #1a73e8', outlineOffset:-2}:{ whiteSpace:'normal', wordBreak:'break-word' }}
                  onClick={()=>{ setSelectedRowId(row.id); setSelectedCell({id:row.id, field:'name'}); }}
                >
                  {editingCell&&editingCell.id===row.id&&editingCell.field==='name' ? (
                    <input
                      style={{width:'100%',fontSize:'inherit',border:'none',outline:'none'}}
                      value={editingCell.value}
                      autoFocus
                      onChange={e=>setEditingCell({...editingCell,value:e.target.value})}
                      onBlur={()=>{
                        updateRowField(editingCell.id, 'name', editingCell.value);
                        setEditingCell(null);
                      }}
                    />
                  ) : row.name}
                </TableCell>
                <TableCell
                  sx={selectedCell?.id===row.id&&selectedCell.field==='unit'?{outline:'2px solid #1a73e8', outlineOffset:-2}:{}}
                  onClick={()=>{setSelectedRowId(row.id); setSelectedCell({id:row.id, field:'unit'});}}
                >
                  {editingCell&&editingCell.id===row.id&&editingCell.field==='unit' ? (
                    <input
                      style={{width:'100%',fontSize:'inherit',border:'none',outline:'none'}}
                      value={editingCell.value}
                      autoFocus
                      onChange={e=>setEditingCell({...editingCell,value:e.target.value})}
                      onBlur={()=>{ updateRowField(editingCell.id,'unit',editingCell.value); setEditingCell(null);} }
                    />
                  ) : row.unit}
                </TableCell>
                <TableCell
                  align="right"
                  sx={selectedCell?.id===row.id&&selectedCell.field==='price'?{outline:'2px solid #1a73e8', outlineOffset:-2}:{}}
                  onClick={()=>{setSelectedRowId(row.id); setSelectedCell({id:row.id, field:'price'});}}
                >
                  {editingCell&&editingCell.id===row.id&&editingCell.field==='price'? (
                    <input
                      style={{width:'100%',textAlign:'right',fontSize:'inherit',border:'none',outline:'none'}}
                      value={editingCell.value}
                      autoFocus
                      onChange={e=>setEditingCell({...editingCell,value:e.target.value})}
                      onBlur={()=>{ const numeric=parseFloat(editingCell.value.replace(',','.'))||0; updateRowField(editingCell.id,'price',numeric); setEditingCell(null);} }
                    />
                  ): row.price.toFixed(2)}
                </TableCell>
                <TableCell align="right">{row.quantity.toFixed(3)}</TableCell>
                <TableCell align="right">{row.value.toLocaleString(undefined,{minimumFractionDigits:2})}</TableCell>
                <TableCell align="center">
                  <IconButton size="small" onClick={e=>handleMenuOpen(e,row.id)}>
                    <MoreVert fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Suma */}
      <Box sx={{ position:'fixed', bottom: 72, right:0, left:0, textAlign:'right', py:1, pr:20, fontWeight:700, borderTop:1, borderColor:'divider', zIndex:1201 }}>
        Razem: {totalValue.toLocaleString(undefined, { minimumFractionDigits: 2 })} zł
      </Box>

      <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={handleMenuClose} anchorOrigin={{vertical:'bottom',horizontal:'right'}} transformOrigin={{vertical:'top',horizontal:'right'}}>
        <MenuItem onClick={handleShowPositions}>Pozycje</MenuItem>
        <MenuItem onClick={handleDeleteRow}>Usuń</MenuItem>
      </Menu>

      <Dialog open={dialogOpen} onClose={()=>setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Pozycje dla nakładu „{selectedObj?.name}”</DialogTitle>
        <DialogContent>
          <List dense>
            {selectedObj?.positions.map(p=>(<ListItem key={p}>Pozycja {p}</ListItem>))}
          </List>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default RMSTab;
